import { getAddress } from "viem";
import { z } from "zod";

export const coldInviteEntrySearchSchema = z.object({
	coldPieceCid: z.string().optional().default(""),
	coldInvite: z.string().optional().default(""),
	email: z.string().optional().default(""),
	platformInvite: z.string().optional().default(""),
	setup: z.string().optional().default(""),
	orgInvite: z.string().optional().default(""),
	/** Set to `"1"` when user chose "Continue anyway" after invite mismatch-skip document redirect. */
	skipColdSign: z.string().optional().default(""),
	upgrade: z.string().optional(),
	interval: z.string().optional(),
});

export type ColdInviteEntrySearch = z.infer<typeof coldInviteEntrySearchSchema>;

/** Query value for {@link ColdInviteEntrySearch.skipColdSign} when deferring cold document open. */
export const SKIP_COLD_SIGN_AFTER_MISMATCH = "1";

export function shouldSkipColdDocumentAfterMismatch(
	search: ColdInviteEntrySearch,
): boolean {
	return search.skipColdSign?.trim() === SKIP_COLD_SIGN_AFTER_MISMATCH;
}

/** Cold sign URL unless user explicitly skipped opening the document after a mismatch. */
export function signDocumentSearchFromColdEntry(
	search: ColdInviteEntrySearch,
): SignDocumentColdSearch | null {
	if (shouldSkipColdDocumentAfterMismatch(search)) return null;
	return toSignDocumentSearch(search);
}

export function hasColdReturn(search: ColdInviteEntrySearch): boolean {
	const c = search.coldPieceCid?.trim();
	const i = search.coldInvite?.trim();
	return Boolean(c && i);
}

export type SignDocumentColdSearch = { pieceCid: string; invite: string };

export function toSignDocumentSearch(
	search: ColdInviteEntrySearch,
): SignDocumentColdSearch | null {
	const pieceCid = search.coldPieceCid?.trim() ?? "";
	const invite = search.coldInvite?.trim() ?? "";
	if (!pieceCid || !invite) return null;
	return { pieceCid, invite };
}

export function buildColdInviteMagicLink(
	origin: string,
	args: { pieceCid: string; inviteToken: string; email?: string },
): string {
	const u = new URL("/", origin);
	u.searchParams.set("coldPieceCid", args.pieceCid);
	u.searchParams.set("coldInvite", args.inviteToken);
	if (args.email?.trim()) {
		u.searchParams.set("email", args.email.trim().toLowerCase());
	}
	return u.toString();
}

function searchParamsFromLocationSearch(search: string): URLSearchParams {
	const raw = search.startsWith("?") ? search.slice(1) : search;
	return new URLSearchParams(raw);
}

/** Parse cold-invite query from a location `search` string (with or without leading `?`). */
export function parseColdInviteFromLocationSearch(
	search: string,
): { coldPieceCid: string; coldInvite: string } | undefined {
	const params = searchParamsFromLocationSearch(search);
	let coldPieceCid = params.get("coldPieceCid")?.trim() ?? "";
	let coldInvite = params.get("coldInvite")?.trim() ?? "";
	if (!coldPieceCid || !coldInvite) {
		const pieceCid = params.get("pieceCid")?.trim() ?? "";
		const invite = params.get("invite")?.trim() ?? "";
		if (pieceCid && invite) {
			coldPieceCid = pieceCid;
			coldInvite = invite;
		}
	}
	if (!coldPieceCid || !coldInvite) return undefined;
	return { coldPieceCid, coldInvite };
}

/**
 * True if the current user is allowed for this cold invite: sender wallet matches, or
 * Linked Auth / Google email matches a recipient row, or Filosign profile email matches.
 */
export function coldInviteRecipientMatchesIdentity(args: {
	recipientEmails: readonly string[];
	loggedInEmail: string;
	profileEmail?: string | null | undefined;
	senderWallet?: string | null | undefined;
	inviteSender: string;
}): boolean {
	const set = new Set(
		args.recipientEmails.map((e) => e.trim().toLowerCase()).filter(Boolean),
	);
	if (args.senderWallet?.trim() && args.inviteSender.trim()) {
		try {
			if (getAddress(args.senderWallet) === getAddress(args.inviteSender)) {
				return true;
			}
		} catch {
			/* invalid address */
		}
	}
	const logged = args.loggedInEmail.trim().toLowerCase();
	if (logged && set.has(logged)) return true;
	const profile = (args.profileEmail ?? "").trim().toLowerCase();
	if (profile && set.has(profile)) return true;
	return false;
}

/** True when URL or session carries sign-in entry params worth preserving across logout. */
export function hasSignInEntryParams(search: ColdInviteEntrySearch): boolean {
	return Boolean(
		search.platformInvite?.trim() ||
			search.setup?.trim() ||
			search.orgInvite?.trim() ||
			(search.coldInvite?.trim() && search.coldPieceCid?.trim()) ||
			search.email?.trim(),
	);
}

/**
 * Reads partner/cold/setup params from the current URL for switch-account flows.
 * Call synchronously before async logout (same timing as {@link extractColdInviteSearchFromLocation}).
 */
export function extractSignInEntrySearchFromLocation():
	| ColdInviteEntrySearch
	| undefined {
	if (typeof window === "undefined") return undefined;
	const params = searchParamsFromLocationSearch(window.location.search);
	const parsed = coldInviteEntrySearchSchema.safeParse({
		coldPieceCid: params.get("coldPieceCid") ?? "",
		coldInvite: params.get("coldInvite") ?? "",
		email: params.get("email") ?? "",
		platformInvite: params.get("platformInvite") ?? "",
		setup: params.get("setup") ?? "",
		orgInvite: params.get("orgInvite") ?? "",
		skipColdSign: params.get("skipColdSign") ?? "",
		upgrade: params.get("upgrade") ?? undefined,
		interval: params.get("interval") ?? undefined,
	});
	if (!parsed.success || !hasSignInEntryParams(parsed.data)) {
		return undefined;
	}
	return parsed.data;
}

/**
 * Reads cold-invite params from the current URL (`coldPieceCid` + `coldInvite`), or maps
 * `/dashboard/document/sign` query `pieceCid` + `invite` into the same shape.
 *
 * Call synchronously before async work (e.g. Auth logout) that may redirect and clear
 * `window.location.search`.
 */
export function extractColdInviteSearchFromLocation():
	| {
			coldPieceCid: string;
			coldInvite: string;
	  }
	| undefined {
	if (typeof window === "undefined") return undefined;
	return parseColdInviteFromLocationSearch(window.location.search);
}
